import { useEffect, useState } from 'react';

import { getInsightComments } from 'api/generated/insight-comments/insight-comments';
import type { InsightCommentDto } from 'api/generated/schemas/insightCommentDto';

import { useCurrentUser } from 'state/current-user/current-user.thunk';

export type InsightComment = InsightCommentDto;

interface UseInsightCommentsArgs {
  insightId: string | number | undefined;
}

interface UseInsightCommentsResult {
  comments: InsightComment[];
  loading: boolean;
  error: unknown;
  refetch: () => Promise<void>;
}

export const useInsightComments = ({
  insightId,
}: UseInsightCommentsArgs): UseInsightCommentsResult => {
  const [comments, setComments] = useState<InsightComment[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<unknown>(null);

  const currentUser = useCurrentUser();
  const userId = currentUser?.data?.oktaUserId;

  const loadComments = async (): Promise<void> => {
    if (!userId || !insightId) return;

    setLoading(true);
    setError(null);

    try {
      const list = await getInsightComments(userId, Number(insightId), {
        sort: ['createdDate,asc'],
      });

      setComments(list ?? []);
    } catch (err) {
      console.error('Error loading insight comments:', err);
      setError(err);
      setComments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadComments();
  }, [userId, insightId]);

  return {
    comments,
    loading,
    error,
    refetch: loadComments,
  };
};
